import { Button, Form, Col, Row } from "react-bootstrap";
import React, { useState, useEffect } from 'react'
import { useTranslation } from "react-i18next";
import 'bootstrap-icons/font/bootstrap-icons.css';
import { Topic } from "../types";
import { useModal } from "../../../hooks/useModal";
import ModalGlobal from "../../../components/AppModal/ModalGlobal";
import { getTopicsSelect } from "../api/getTopics";
import { ApiErrorResponse } from "../../../types/ApiErrorResponse";
import SentimentIndicator from "../../../components/SentimentIndicatorSelector";
import GenerateAlertSelector from "../../../components/GenerateAlertSelctor";
import { useCurrentClient } from "../../auth/api/getCurrentClient";
import { TreeSelectItemDto } from "../../../types/SelectItemDto";

type DetailTopicProps = {
    indexField: number;
    topics: Topic[];
    setTopics: React.Dispatch<React.SetStateAction<Topic[]>>;
    editing: boolean;
    onChangeAlertAI?: (value: number | null) => void;
};

export default function DetailTopic({ indexField, topics, setTopics, editing, onChangeAlertAI }: DetailTopicProps) {
    const { t } = useTranslation(['common', 'topic']); 
    const { data: currentClient } = useCurrentClient();
    const [topicsAvailable, setTopicsAvailable] = useState<TreeSelectItemDto[]>([]);
    const [isNewTopic, setIsNewTopic] = useState<boolean>(false);
    const [isNewSubTopic, setIsNewSubTopic] = useState<boolean>(false);
    const [error, setError] = useState<string>();
    const topicSentimentValue = topics[indexField].sentimentAI
        ? Number(topics[indexField].sentimentAI)
        : null;
    const topicAlertValue = topics[indexField].alertAI
        ? Number(topics[indexField].alertAI)
        : null;


    useEffect(() => {
        const loadTopicsAvailable = async() => {
            await getTopicsSelect()
                .then(setTopicsAvailable)
                .catch((error: ApiErrorResponse) => {
                    const errorMessage = error.message ?? t("common:systemError");
                    setError(errorMessage);
                })
        }
        loadTopicsAvailable();
    }, []);

    const updateTopicField = async <K extends keyof Topic>(field: K, value: Topic[K]) => {
        const newTopics = [...topics];
        newTopics[indexField] = {
            ...newTopics[indexField],
            [field]: value,
        };

        if (field === 'topicName') {
            newTopics[indexField].topicId = -1;
            updateTopicField('subtopicId', -2);
        }
        else if (field === 'subtopicName') {
            newTopics[indexField].subtopicId = -1;
        }
        else if (field === 'topicId') {
            updateTopicField('subtopicId', -2);
        } else if (field == 'alertAI') {
            const parsedValue = value == null || value === "" ? null : Number(value);
            const normalizedValue = parsedValue !== null && Number.isNaN(parsedValue) ? null : parsedValue;
            onChangeAlertAI?.(normalizedValue);
        }

        setTopics(newTopics);
    };   
    
    const handleNewTopic = () => {
        setIsNewTopic(true);
        updateTopicField('topicName', '');
    }

    const handleNewSubTopic = () => {
        setIsNewSubTopic(true);
        updateTopicField('subtopicName', '');
    }

    const { showModal, modalData, errorModal, endDate, closeModal, openModal: openDeleteModal, isLoading } = useModal<Topic>();

    const handleDeleteTopic = async (topic: Topic) => {
        if (topic.topicId != -2 || topic.topicName || topic.alertAI || topic.sentimentAI) {
            openDeleteModal(
                topic,
                t("survey:modal.modalDeleteTopicTitle"),
                t("survey:modal.modalDeleteTopicSubtitle", { name: topic.topicName || topicsAvailable.find(x => x.id == topic.topicId)?.name }),
                "info",
                ""
            );
        } else {
            handleConfirmDeleteTopic();
        }
    };
    
    const handleConfirmDeleteTopic = async () => {
        const updatedTopics = topics.filter((_, index) => index !== indexField);
        setTopics(updatedTopics);
        closeModal();
    };
    return (
        <>
            <Row>
                <ModalGlobal
                    show={showModal}
                    onClose={closeModal}
                    title={modalData.title}
                    contentType={modalData.type}
                    subtitle={modalData.subtitle}
                    subtitle2={modalData.subtitle2}
                    saveText={t("common:modalActionOK")}
                    closeText={t("common:modalActionNOTOK")}
                    error={errorModal?.endDate || errorModal?.errorModal}
                    endDate={endDate}
                    onSave={() => handleConfirmDeleteTopic()}
                    isLoading={isLoading}
                />
                {error && <small className="text-danger">{error}</small>}
                <Col xs={3}>
                    <Form.Label>{t("topic:topic")}# {indexField +1}</Form.Label>                        
                    {!isNewTopic && topics[indexField]?.topicId !== -1 && (
                        <Form.Select
                            className="form-control"
                            value={topics[indexField]?.topicId ?? ""}
                            disabled={!editing}
                            onChange={(e) => {
                                const selected = Number(e.target.value);
                                if (selected === -1) {
                                    handleNewTopic(); // Esto s� se ejecutar� correctamente
                                } else {
                                    updateTopicField('topicId', selected);
                                }
                            }}
                        >
                            <option
                                value="-2" disabled>{t("common:selectDefaultText")}</option> 
                            <option className="option-new-topic"
                                value="-1">{t("topic:newTopic")}</option> 
                            {topicsAvailable.filter(topic => topic.parentId == null).map((topic, i) => (
                                <option key={i} value={topic.id}>{topic.name}</option>
                            ))}
                        </Form.Select>
                    )}
                    {isNewTopic && (
                        <>
                            <Form.Control
                                type="text"
                                className="form-control"
                                value={topics[indexField]?.topicName ?? ""}
                                onChange={(e) => updateTopicField('topicName', e.target.value)}
                            />
                        </>
                    )}                    
                </Col>
                <Col xs={3}>
                    <Form.Label>{t("topic:subtopic")}# {indexField +1}</Form.Label>                        
                    {!isNewSubTopic && (
                        <Form.Select
                            className="form-control"
                            value={topics[indexField]?.subtopicId ?? ""}
                            disabled={!editing || (!topics[indexField]?.topicId && !topics[indexField]?.topicName)}
                            onChange={(e) => {
                                const selected = Number(e.target.value);
                                if (selected === -1) {
                                    handleNewSubTopic();
                                } else {
                                    updateTopicField('subtopicId', selected);
                                }
                            }}
                        >
                            <option
                                value="-2" disabled>{t("common:selectDefaultText")}</option> 
                            <option className="option-new-topic"
                                value="-1">{t("topic:newSubtopic")}</option> 
                            {topicsAvailable.filter(topic => topic.parentId == topics[indexField]?.topicId).map((topic, i) => (
                                <option key={i} value={topic.id}>{topic.name}</option>
                            ))}
                        </Form.Select>
                    )}
                    {isNewSubTopic && (
                        <>
                            <Form.Control
                                type="text"
                                className="form-control"
                                value={topics[indexField]?.subtopicName ?? ""}
                                onChange={(e) => updateTopicField('subtopicName', e.target.value)}
                            />
                        </>
                    )}                    
                </Col>
                <Col xs={2} >                    
                    <Form.Group>
                        <Form.Label>{t("topic:sentiment")}</Form.Label>
                        <SentimentIndicator
                            value={topicSentimentValue}
                            onChange={(newValue) => updateTopicField('sentimentAI', newValue ?? undefined)}
                            disabled={!editing}
                            minValue={currentClient?.sentimentMinimumValue}
                            maxValue={currentClient?.sentimentMaximumValue}
                        />        
                    </Form.Group>
                </Col>
                <Col xs={3}>
                    <Form.Group>
                        <Form.Label>{t("topic:alert")}</Form.Label>
                        <GenerateAlertSelector
                            value={topicAlertValue}
                            onChange={(newValue) => updateTopicField('alertAI', newValue ?? undefined)}
                            disabled={!editing}
                            minValue={currentClient?.sentimentMinimumValue}
                            maxValue={currentClient?.sentimentMaximumValue}
                            neutralReference={currentClient?.neutralReference}
                            alertMinimumValue={currentClient?.alertMinimumValue}
                            alertMaximumValue={currentClient?.alertMaximumValue}
                            showSelectedLabel={true}
                            compactSelected={false}
                        />
                    </Form.Group>
                </Col>
                <Col xs={1} className="d-flex justify-content-end" style={{
                    marginTop: '43px'
                }} >
                    {editing && (
                        <Button onClick={() => handleDeleteTopic(topics[indexField])} disabled={!editing} variant="secondary">
                            <i className="bi bi-x"></i>
                        </Button>
                    )}
                </Col>        
            </Row>
        </>
    );
}


